import { Badge, Button, Group, Popover, ScrollArea, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconHistory } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useSubscription } from 'urql';
import { FlowPublicationsSubscription } from './graphql';

type Publisher = { firstName: string | null; lastName: string | null; email: string };

function publisherName(user: Publisher | null | undefined) {
  if (!user) return null;

  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
}

function PublicationRow({
  publishedAt,
  publisher,
  isCurrent,
}: {
  publishedAt: string;
  publisher: Publisher | null | undefined;
  isCurrent: boolean;
}) {
  const name = publisherName(publisher);

  return (
    <Group justify="space-between" wrap="nowrap" gap="xs" py={4}>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm">{dayjs(publishedAt).format('MMM D, YYYY h:mm A')}</Text>
        {name && (
          <Text size="xs" c="dimmed" truncate>
            by {name}
          </Text>
        )}
      </Stack>
      {isCurrent && (
        <Badge size="xs" color="green" variant="light">
          Live
        </Badge>
      )}
    </Group>
  );
}

export function FlowVersions({
  flowId,
  currentPublicationId,
}: {
  flowId: string;
  currentPublicationId: string | null;
}) {
  const [{ data }] = useSubscription({ query: FlowPublicationsSubscription, variables: { flowId } });

  const publications = data?.flowPublications ?? [];

  return (
    <Popover width={320} position="bottom-start" shadow="md" withArrow>
      <Popover.Target>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconHistory size={16} />}
          rightSection={<IconChevronDown size={12} />}
        >
          Versions
          {publications.length > 0 ? ` (${publications.length})` : ''}
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="sm">
        {publications.length === 0 ? (
          <Text size="sm" c="dimmed">
            This flow has not been published yet.
          </Text>
        ) : (
          <ScrollArea.Autosize mah={300}>
            <Stack gap={2}>
              {publications.map((publication) => (
                <PublicationRow
                  key={publication.id}
                  publishedAt={publication.publishedAt}
                  publisher={publication.publishedByUser}
                  isCurrent={publication.id === currentPublicationId}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
